/* Tab Lịch hẹn: lịch sắp tới, lịch trước đó (trên máy + hệ thống), tạo / dời / huỷ lịch.
   Form cùng trường với màn Tạo lịch hẹn (/appointment/create); trạng thái cùng tên với /appointments. */
import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Checkbox, DatePicker, Empty, Input, InputNumber, Modal, Select, Spin, Tag, Tooltip, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { CalendarClock, CalendarPlus, ChevronDown, ChevronUp, MapPin, Repeat2, Trash2, UploadCloud, UserX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getAppointments, type AppointmentItem } from '../../../api/Appointment';
import type { ChannelCustomerItem } from '../../../api/ChannelCustomer';
import { useLocationData } from '../../../hooks/useLocationData';
import { TPL } from '../components/Data';
import type { Booking } from './BookingFields';
import { Chips, Lbl } from './QuoteTab';
import {
  CANCEL_REASONS, GENDERS, PERIODS, SLOTS, SLOT_CAP, SYS_ST, apptInputError, apptStats, bookableQuotes, overdue, quoteNote, quoteSvcs, sameDayLive,
  slotLabel, slotLoad, splitAppts, suggestType, svcTotal, toApiPayload, withQuoteNote, type ApptCustomer, type ApptInput, type ApptRef, type ApptSvc,
} from './appt';
import { QUOTE_ST, checkQuote, money } from './quote';
import { postAppointment, putAppointment, searchServices, useApptRefs, type Opt, type SvcOpt } from './apptRefs';
import { isDemoId } from './demo';
import { HOUR, MANGS, OUTCOMES, confirmDue, staffOf, when, type ApptSt, type LeadAppt, type LeadLocal, type Mang } from './logic';
import { useLeadStore } from './store';

const WD = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
const WD_FULL = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
/** Trạng thái lịch trên máy — 3 trạng thái đầu cùng tên / màu với /appointments */
const APPT_ST: Record<ApptSt, { label: string; color: string }> = {
  booked: SYS_ST[1],
  confirmed: SYS_ST[2],
  cancelled: SYS_ST[0],
  arrived: { label: 'Đã đến cơ sở', color: 'geekblue' },
  noshow: { label: 'Không đến', color: 'volcano' },
  done: { label: 'Đã tư vấn', color: 'purple' },
};
const vnd = (n: number) => `${n.toLocaleString('vi-VN')} đ`;
const fullDay = (at: number) => `${WD_FULL[dayjs(at).day()]}, ${dayjs(at).format('DD/MM/YYYY')}`;
const relDay = (at: number, now: number) => {
  const d = dayjs(at).startOf('day').diff(dayjs(now).startOf('day'), 'day');
  return d === 0 ? (at < now ? 'Đang diễn ra' : 'Hôm nay') : d === 1 ? 'Ngày mai' : `Còn ${d} ngày`;
};
const svcText = (a: LeadAppt) => (a.services?.length ? a.services.map((s) => (s.qty > 1 ? `${s.name} ×${s.qty}` : s.name)).join(', ') : a.service);
const people = (a: LeadAppt) => [
  a.telesale && `Telesale: ${a.telesale.name}`, a.staff && `Trực page: ${a.staff.name}`, a.branchStaff && `NV cơ sở: ${a.branchStaff.name}`,
].filter(Boolean).join(' · ');

/** Lịch hẹn của khách trên hệ thống (chỉ đọc); khách demo không gọi API */
function useServerAppts(id: number, tick: number) {
  const demo = isDemoId(id);
  const [st, setSt] = useState<{ loading: boolean; failed?: boolean; items: AppointmentItem[] }>({ loading: !demo, items: [] });
  useEffect(() => {
    if (demo) return undefined;
    let alive = true;
    setSt({ loading: true, items: [] });
    getAppointments({ customer_id: id, limit: 20 }).then((res) => {
      if (!alive) return;
      // Lọc lại phía máy phòng khi API bỏ qua tham số
      setSt({ loading: false, failed: !res.status, items: (res.data?.items ?? []).filter((x) => x.channel_customer_id === id || x.customer?.id === id) });
    });
    return () => { alive = false; };
  }, [id, demo, tick]);
  return st;
}

/* ---------- Ô lịch ---------- */

const DateBlock = ({ at, tone }: { at: number; tone: 'up' | 'past' | 'off' }) => (
  <div className={`flex w-12 shrink-0 flex-col items-center self-start rounded-lg py-1 ${tone === 'up' ? 'bg-[#3e91ab] text-white' : tone === 'past' ? 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200' : 'bg-gray-50 text-gray-400 dark:bg-gray-900/40'}`}>
    <span className="text-[10.5px] font-semibold uppercase opacity-80">{WD[dayjs(at).day()]}</span>
    <span className="text-[18px] font-bold leading-tight">{dayjs(at).format('DD')}</span>
    <span className="text-[10.5px] opacity-80">Th{dayjs(at).format('M')}</span>
  </div>
);

/** Lịch đã lên hệ thống: hiện mã, bấm mở chi tiết lịch hẹn; chưa lên thì báo để đưa lên */
const SyncBadge = ({ a }: { a: LeadAppt }) => (a.serverId ? (
  <Tooltip title="Đã có ở danh sách Lịch hẹn — bấm để mở">
    <Link to={`/appointment/detail/${a.serverId}`} className="rounded bg-emerald-50 px-1 py-px text-[10.5px] font-semibold text-emerald-700 hover:underline dark:bg-emerald-500/10 dark:text-emerald-400">
      {a.serverCode || `#${a.serverId}`}
    </Link>
  </Tooltip>
) : (
  <Tooltip title="Lịch chỉ lưu trên máy này, chưa có ở danh sách Lịch hẹn">
    <span className="rounded bg-amber-50 px-1 py-px text-[10.5px] font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">Chưa lên hệ thống</span>
  </Tooltip>
));

function UpCard({ a, all, now, readOnly, onConfirm, onMove, onCancel, onSync }: {
  a: LeadAppt; all: LeadAppt[]; now: number; readOnly?: boolean; onConfirm: () => void; onMove: () => void; onCancel: () => void; onSync?: () => void;
}) {
  const due = a.status === 'booked' ? confirmDue(a.at, a.createdAt) : undefined;
  const from = a.movedFrom ? all.find((x) => x.id === a.movedFrom) : undefined;
  const svc = svcText(a);
  return (
    <div className="flex gap-3 rounded-lg border border-[#3e91ab]/40 bg-[#e8f4f7]/50 p-3 dark:bg-[#3e91ab]/10">
      <DateBlock at={a.at} tone="up" />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[15px] font-bold text-gray-900 dark:text-gray-50">{dayjs(a.at).format('HH:mm')} <span className="text-[12.5px] font-medium text-gray-500">· {relDay(a.at, now)}</span></div>
            <div className="flex items-center gap-1 text-[12.5px] text-gray-600 dark:text-gray-300"><MapPin size={12} className="shrink-0" />{a.branch}</div>
          </div>
          <Tag color={APPT_ST[a.status].color} className="m-0 shrink-0">{APPT_ST[a.status].label}</Tag>
        </div>
        {svc && <div className="mt-1 text-[12.5px] text-gray-700 dark:text-gray-200">{svc}{a.services?.length ? <span className="text-gray-500"> · {vnd(svcTotal(a.services))}</span> : null}</div>}
        {people(a) && <div className="mt-0.5 text-[12px] text-gray-500">{people(a)}</div>}
        {a.note && <div className="mt-1 text-[12px] italic text-gray-500">“{a.note}”</div>}
        {due && (
          <div className={`mt-1.5 text-[12px] font-medium ${due < now ? 'text-red-600' : 'text-amber-600'}`}>
            {due < now ? 'Cần gọi xác nhận ngay' : `Gọi xác nhận trước ${dayjs(due).format('HH:mm DD/MM')}`}
          </div>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-x-1.5 text-[11px] text-gray-400">
          <SyncBadge a={a} />
          <span>Đặt bởi {a.by} · {when(a.createdAt, now)}{from ? ` · dời từ ${dayjs(from.at).format('HH:mm DD/MM')}` : ''}</span>
        </div>
        {!readOnly && a.at > now && (
          <div className="mt-2 flex flex-wrap gap-2">
            {!a.serverId && onSync && <Button size="small" type="primary" icon={<UploadCloud size={13} />} className="!bg-amber-500" onClick={onSync}>Đưa lên hệ thống</Button>}
            {a.status === 'booked' && <Button size="small" type="primary" className="!bg-[#3e91ab]" onClick={onConfirm}>Khách xác nhận</Button>}
            <Button size="small" icon={<Repeat2 size={13} />} onClick={onMove}>Dời lịch</Button>
            <Button size="small" danger onClick={onCancel}>Huỷ lịch</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function PastRow({ a, all, now, readOnly, onNoShow, onRebook }: {
  a: LeadAppt; all: LeadAppt[]; now: number; readOnly?: boolean; onNoShow: () => void; onRebook: () => void;
}) {
  const late = overdue(a, now);
  const to = a.movedTo ? all.find((x) => x.id === a.movedTo) : undefined;
  const off = a.status === 'cancelled';
  const svc = svcText(a);
  const extra = [
    a.consultant && `Tư vấn: ${a.consultant}`,
    to && `Dời sang ${dayjs(to.at).format('HH:mm DD/MM')}`,
    a.cancelReason && `Lý do: ${a.cancelReason}`,
  ].filter(Boolean).join(' · ');
  // Đặt lại khi lịch không thành (không đến / huỷ hẳn / quá giờ chưa cập nhật)
  const canRebook = !readOnly && (late || a.status === 'noshow' || (off && !a.movedTo));
  return (
    <li className="flex gap-3 py-2.5">
      <DateBlock at={a.at} tone={off ? 'off' : 'past'} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className={`min-w-0 text-[13px] ${off ? 'text-gray-400' : 'text-gray-800 dark:text-gray-100'}`}>
            <span className={`font-semibold ${off ? 'line-through' : ''}`}>{dayjs(a.at).format('HH:mm')}</span>
            <span className="text-gray-500"> · {a.branch}</span>
          </div>
          {late
            ? <Tooltip title="Đã qua giờ hẹn nhưng chưa ghi khách đến hay không"><Tag color="orange" className="m-0 shrink-0">Quá giờ · chưa cập nhật</Tag></Tooltip>
            : <Tag color={a.movedTo ? 'purple' : APPT_ST[a.status].color} className="m-0 shrink-0">{a.movedTo ? 'Đã dời' : APPT_ST[a.status].label}</Tag>}
        </div>
        {svc && <div className="text-[12px] text-gray-600 dark:text-gray-300">{svc}</div>}
        <div className="text-[11.5px] text-gray-400">{people(a) || `Đặt bởi ${a.by}`}</div>
        {a.outcome && (
          <div className="mt-0.5 text-[12.5px] font-medium text-gray-700 dark:text-gray-200">
            Kết quả: {OUTCOMES[a.outcome.kind].label}{a.outcome.amount ? ` · ${vnd(a.outcome.amount)}` : ''}{a.outcome.note ? ` · “${a.outcome.note}”` : ''}
          </div>
        )}
        {extra && <div className="mt-0.5 text-[12px] text-gray-500">{extra}</div>}
        {(canRebook || late) && !readOnly && (
          <div className="mt-1.5 flex flex-wrap gap-2">
            {late && <Button size="small" icon={<UserX size={13} />} onClick={onNoShow}>Ghi không đến</Button>}
            {canRebook && <Button size="small" icon={<CalendarPlus size={13} />} onClick={onRebook}>Đặt lại lịch</Button>}
          </div>
        )}
      </div>
    </li>
  );
}

function ServerRow({ s, now }: { s: AppointmentItem; now: number }) {
  const at = dayjs(s.scheduled_at).valueOf();
  // Cùng cột Trạng thái của /appointments: trạng thái khách theo thẻ, không có thì theo trạng thái lịch
  const tagSt = s.customer_tag_status ?? null;
  const st = tagSt != null ? SYS_ST[tagSt] : SYS_ST[s.status];
  const label = s.customer_tag_status_text || s.status_text || st?.label || `Trạng thái ${s.status}`;
  const svc = s.services?.map((x) => x.name).join(', ') || s.service?.name;
  const who = [
    s.telesale && `Telesale: ${s.telesale.fullname || s.telesale.name}`,
    s.staff && `Trực page: ${s.staff.fullname || s.staff.name}`,
    s.branch_staff && `NV cơ sở: ${s.branch_staff.fullname || s.branch_staff.name}`,
  ].filter(Boolean).join(' · ');
  return (
    <li className="flex gap-3 py-2.5">
      <DateBlock at={at} tone={at >= now && s.status !== 0 ? 'up' : s.status === 0 ? 'off' : 'past'} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 text-[13px] text-gray-800 dark:text-gray-100">
            <span className="font-semibold">{dayjs(at).format('HH:mm')}</span>
            {s.branch?.name && <span className="text-gray-500"> · {s.branch.name}</span>}
          </div>
          <Tag color={st?.color} className="m-0 shrink-0">{label}</Tag>
        </div>
        {svc && <div className="text-[12px] text-gray-600 dark:text-gray-300">{svc}</div>}
        {who && <div className="text-[11.5px] text-gray-400">{who}</div>}
        <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11.5px] text-gray-400">
          <a href={`/appointment/detail/${s.id}`} className="rounded bg-slate-100 px-1 py-px font-semibold text-slate-600 hover:text-[#3e91ab] dark:bg-slate-700 dark:text-slate-300">{s.code}</a>
          {s.customer_tag?.name && <Tag color={s.customer_tag.color || undefined} className="m-0 text-[11px]">{s.customer_tag.name}</Tag>}
        </div>
        {s.note && <div className="mt-0.5 text-[12px] italic text-gray-500">“{s.note}”</div>}
      </div>
    </li>
  );
}

/* ---------- Form tạo / dời lịch (cùng trường màn Tạo lịch hẹn) ---------- */

export interface ApptSave { booking: Booking; extra: Partial<LeadAppt> }

const Sec = ({ title, extra, children }: { title: string; extra?: React.ReactNode; children: React.ReactNode }) => (
  <section className="space-y-2.5 border-t border-gray-100 pt-3 first:border-0 first:pt-0 dark:border-gray-700">
    <div className="flex items-center justify-between gap-2 text-[13px] font-semibold text-gray-800 dark:text-gray-100">{title}{extra}</div>
    {children}
  </section>
);
const F = ({ label, required, full, children }: { label: string; required?: boolean; full?: boolean; children: React.ReactNode }) => (
  <label className={`flex min-w-0 flex-col gap-1 ${full ? 'col-span-full' : ''}`}>
    <span className="text-[12px] font-medium text-gray-500 dark:text-gray-400">{required && <span className="mr-0.5 text-red-500">*</span>}{label}</span>
    {children}
  </label>
);
/** Giữ giá trị đang chọn hiển thị đúng tên dù chưa có trong danh sách tải về */
const withCur = (opts: Opt[], cur?: ApptRef) => (cur && !opts.some((o) => o.value === cur.id) ? [{ value: cur.id, label: cur.name }, ...opts] : opts);
const pick = (opts: Opt[], id?: number): ApptRef | undefined => {
  const o = opts.find((x) => x.value === id);
  return o ? { id: o.value, name: o.label } : undefined;
};

function customerOf(r: ChannelCustomerItem): ApptCustomer {
  return {
    name: (r.display_name || r.fullname || '').trim(),
    fullname: (r.fullname || '').trim(),
    phone: r.phone || '',
    email: r.email || '',
    gender: r.gender ?? null,
    dob: r.birthday ? dayjs(r.birthday).format('YYYY-MM-DD') : null,
    isForeign: Number(r.is_foreign) === 1,
    province: r.province_code || null,
    ward: r.ward_code || null,
    address: r.address || '',
  };
}

/** Lỗi phần khách hàng (nếu có) — dùng chung kiểm tra của form */
const custGap = (c: ApptCustomer) => apptInputError({ customerId: 0, customer: c, branchId: 1, at: null, services: [], note: '' }, 0)?.replace(/^Chọn thời gian hẹn\.$/, '') || null;

function ApptForm({ r, l, now, mang: mang0, move, base, sync, quoteId, onSwitchMove, onClose, onSave }: {
  r: ChannelCustomerItem;
  l: LeadLocal;
  now: number;
  /** Mảng hiện tại của khách (đã suy từ nguồn) */
  mang?: Mang;
  /** Đang dời lịch này */
  move?: LeadAppt;
  /** Điền sẵn từ lịch cũ (đặt lại) */
  base?: LeadAppt;
  /** Đưa lịch trên máy (base) lên hệ thống thay vì tạo lịch mới */
  sync?: boolean;
  /** Đặt lịch theo báo giá này (bấm từ tab Báo giá); không có thì lấy báo giá mới nhất */
  quoteId?: string;
  onSwitchMove: (a: LeadAppt) => void;
  onClose: () => void;
  /** Trả về lỗi để hiện trong form; không lỗi thì form đóng */
  onSave: (v: ApptSave, opt: { notify: boolean; reason?: string }) => Promise<string | undefined>;
}) {
  const byId = useLeadStore((s) => s.byId);
  const refs = useApptRefs();
  const { provinceOptions, wardOptions, loadingProvinces, loadingWards, loadWards } = useLocationData();
  const src = move ?? base;
  const today = dayjs(now).startOf('day');

  const [cust, setCust] = useState<ApptCustomer>(() => src?.customer ?? customerOf(r));
  const setC = (p: Partial<ApptCustomer>) => setCust((c) => ({ ...c, ...p }));
  // Thiếu thông tin bắt buộc thì mở sẵn phần khách hàng
  const [custOpen, setCustOpen] = useState(() => !!custGap(src?.customer ?? customerOf(r)));

  const [branchId, setBranchId] = useState<number | undefined>(src?.branchId);
  // Hôm nay hết khung nhận lịch thì mặc định ngày mai
  // Đưa lịch trên máy lên hệ thống: giữ nguyên ngày giờ đã hẹn
  const keep = sync && base && base.at > now ? base : undefined;
  const [day, setDay] = useState<Dayjs>(move ? dayjs(move.at).startOf('day') : keep ? dayjs(keep.at).startOf('day') : today.add(SLOTS[SLOTS.length - 1], 'minute').valueOf() <= now ? today.add(1, 'day') : today);
  const [slot, setSlot] = useState<number | undefined>(() => (keep ? dayjs(keep.at).diff(dayjs(keep.at).startOf('day'), 'minute') : undefined));
  // Lịch + khung giờ chung một popup; chọn ngày giữ popup mở, chọn giờ mới đóng
  const [pickerOpen, setPickerOpen] = useState(false);
  const keepOpen = React.useRef(false);
  const [staff, setStaff] = useState<ApptRef | undefined>(src?.staff ?? (r.in_charge_staff || r.staff || undefined));
  const [telesale, setTelesale] = useState<ApptRef | undefined>(() => {
    if (src?.telesale) return src.telesale;
    const t = staffOf(r, l);
    return t ? { id: Number(t.id), name: t.name } : undefined;
  });
  const [branchStaff, setBranchStaff] = useState<ApptRef | undefined>(src?.branchStaff);
  // Lịch mới: lấy toàn bộ dịch vụ + tóm tắt báo giá (mới nhất, hoặc bản vừa bấm "Đặt lịch theo báo giá") vào form
  const quotes = useMemo(() => bookableQuotes(l.quotes), [l.quotes]);
  const [quote, setQuote] = useState(() => (src ? quotes.find((q) => q.code === src.quoteCode) : quotes.find((q) => q.id === quoteId) ?? quotes[0]));
  const [services, setServices] = useState<ApptSvc[]>(() => src?.services ?? (quote ? quoteSvcs(quote) : []));
  const [svcOpts, setSvcOpts] = useState<SvcOpt[]>([]);
  const [svcQ, setSvcQ] = useState('');
  const [svcLoading, setSvcLoading] = useState(false);
  const [mang, setMang] = useState<Mang | undefined>(src?.mang ?? mang0);
  const [note, setNote] = useState(() => src?.note ?? (quote ? quoteNote(quote) : ''));
  const [notify, setNotify] = useState(true);
  const [reason, setReason] = useState<string>();
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Tải phường/xã theo tỉnh của khách
  useEffect(() => { if (cust.province) loadWards(cust.province); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Đặt lại / dời lịch: chi nhánh cũ lưu theo tên → tìm lại id; lịch mới: lấy chi nhánh lần trước
  useEffect(() => {
    if (!refs || branchId) return;
    const name = src?.branch ?? l.appts[0]?.branch;
    const hit = refs.branches.find((b) => b.label === name);
    if (hit) setBranchId(hit.value);
  }, [refs]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    let alive = true;
    setSvcLoading(true);
    const t = setTimeout(() => searchServices(svcQ).then((xs) => { if (alive) { setSvcOpts(xs); setSvcLoading(false); } }), 350);
    return () => { alive = false; clearTimeout(t); };
  }, [svcQ]);
  // Báo giá cũ chưa gắn mã danh mục: tìm theo đúng tên để lịch gửi được dịch vụ lên hệ thống
  const unlinked = services.filter((s) => s.id < 0).map((s) => s.name).join('\n');
  useEffect(() => {
    if (!unlinked) return undefined;
    let alive = true;
    const key = (n: string) => n.trim().toLowerCase();
    Promise.all(unlinked.split('\n').map((n) => searchServices(n).then((xs) => xs.find((x) => x.id > 0 && key(x.name) === key(n))))).then((hits) => {
      const found = new Map(hits.flatMap((h) => (h ? [[key(h.name), h] as const] : [])));
      if (!alive || !found.size) return;
      setServices((xs) => xs.map((x) => {
        const h = x.id < 0 ? found.get(key(x.name)) : undefined;
        return h && !xs.some((y) => y.id === h.id) ? { ...x, id: h.id, price: h.price } : x;
      }));
    });
    return () => { alive = false; };
  }, [unlinked]);

  const branchName = refs?.branches.find((b) => b.value === branchId)?.label;
  const load = useMemo(() => (branchName ? slotLoad(byId, branchName, day.valueOf(), move?.id) : {}), [byId, branchName, day, move?.id]);
  const at = slot === undefined ? null : day.add(slot, 'minute');
  const clash = at ? sameDayLive(l.appts, at.valueOf(), move?.id) : undefined;
  const total = svcTotal(services);
  const quoteAfter = quote ? checkQuote(quote.items, quote.discount, quote.reason).after : null;
  /** Đổi / bỏ báo giá: thay danh sách dịch vụ và dòng tóm tắt trong ghi chú */
  const pickQuote = (id?: string) => {
    const q = quotes.find((x) => x.id === id);
    setQuote(q);
    setServices(q ? quoteSvcs(q) : []);
    setNote((n) => withQuoteNote(n, q ? quoteNote(q) : ''));
  };
  const location = [cust.address, wardOptions.find((w) => w.value === cust.ward)?.label, provinceOptions.find((p) => p.value === cust.province)?.label].filter(Boolean).join(', ');

  const pickDay = (d: Dayjs) => {
    setDay(d.startOf('day'));
    if (slot !== undefined && d.startOf('day').add(slot, 'minute').valueOf() <= now) setSlot(undefined);
  };
  const addSvc = (id: number) => {
    const s = svcOpts.find((x) => x.id === id);
    if (!s) return;
    setServices((xs) => (xs.some((x) => x.id === id) ? xs.map((x) => (x.id === id ? { ...x, qty: x.qty + 1 } : x)) : [...xs, { ...s, qty: 1 }]));
    setSvcQ('');
  };

  const save = async () => {
    const input: ApptInput = {
      customerId: r.id, customer: cust, branchId, at: at ? at.valueOf() : null, staff, telesale, branchStaff, services, note: note.trim(),
    };
    const e = apptInputError(input, now)
      ?? (!mang ? 'Chọn mảng.' : null)
      ?? (move && at?.valueOf() === move.at && branchName === move.branch ? 'Lịch mới trùng giờ cũ — chọn giờ khác.' : null);
    if (e) {
      setErr(e);
      if (/khách hàng|Họ và tên/.test(e)) setCustOpen(true);
      return;
    }
    // Gắn kênh liên kết đầu tiên của khách như màn Tạo lịch hẹn
    const conn = r.connections?.map((c) => c.channel_connection?.id ?? c.channel_customer?.channel_connection_id).find(Boolean);
    const payload = { ...toApiPayload(input), ...(conn ? { channel_connection_id: conn } : {}) };
    setErr(null);
    setSaving(true);
    const fail = await onSave({
      booking: { at, branch: branchName, mang, service: services.map((s) => s.name).join(', '), note: note.trim(), type: src?.type ?? suggestType(l.appts) },
      extra: { branchId, services, staff, telesale, branchStaff, customer: cust, payload, quoteCode: quote?.code },
    }, { notify, reason });
    setSaving(false);
    if (fail) setErr(fail);
  };

  const sel = (opts: Opt[] | undefined, cur: ApptRef | undefined, set: (v?: ApptRef) => void) => (
    <Select
      value={cur?.id}
      onChange={(v) => set(pick(withCur(opts ?? [], cur), v))}
      options={withCur(opts ?? [], cur)}
      loading={!refs}
      placeholder="Vui lòng chọn"
      showSearch
      allowClear
      optionFilterProp="label"
    />
  );

  return (
    <div className="space-y-3 rounded-lg border border-[#3e91ab]/50 p-3 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[14px] font-semibold text-gray-800 dark:text-gray-100">
          {move ? `Dời lịch ${dayjs(move.at).format('HH:mm DD/MM')}` : base ? 'Đặt lại lịch hẹn' : 'Tạo lịch hẹn'}
        </div>
        {refs?.offline && <Tooltip title="Chưa tải được chi nhánh / nhân viên từ hệ thống — đang dùng dữ liệu mẫu"><Tag color="orange" className="m-0">Dữ liệu mẫu</Tag></Tooltip>}
      </div>

      {/* Khách hàng */}
      <Sec
        title="Khách hàng"
        extra={(
          <button type="button" onClick={() => setCustOpen((v) => !v)} className="flex items-center gap-0.5 text-[12px] font-medium text-[#3e91ab] hover:underline">
            {custOpen ? <>Thu gọn<ChevronUp size={14} /></> : <>Sửa thông tin<ChevronDown size={14} /></>}
          </button>
        )}
      >
        {!custOpen ? (
          <button type="button" onClick={() => setCustOpen(true)} className="block w-full rounded-md bg-gray-50 px-3 py-2 text-left text-[12.5px] dark:bg-gray-900/40">
            <div className="font-semibold text-gray-800 dark:text-gray-100">{cust.name || <span className="text-red-500">Thiếu họ và tên</span>}{cust.fullname && cust.fullname !== cust.name ? <span className="font-normal text-gray-500"> · {cust.fullname}</span> : ''}</div>
            <div className="text-gray-600 dark:text-gray-300">{cust.phone || <span className="text-red-500">Thiếu số điện thoại</span>}{cust.email ? ` · ${cust.email}` : ''}{cust.isForeign ? ' · Khách nước ngoài' : ''}</div>
            <div className="text-gray-500">{location || '—'}</div>
            {custGap(cust) && <div className="mt-0.5 text-red-500">{custGap(cust)}</div>}
          </button>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <F label="Họ và tên" required><Input value={cust.name} onChange={(e) => setC({ name: e.target.value })} placeholder="Họ và tên" /></F>
            <F label="Họ tên thật"><Input value={cust.fullname} onChange={(e) => setC({ fullname: e.target.value })} placeholder="Họ tên thật" /></F>
            <F label="Số điện thoại" required><Input value={cust.phone} onChange={(e) => setC({ phone: e.target.value })} placeholder="Số điện thoại" /></F>
            <F label="Email"><Input value={cust.email} onChange={(e) => setC({ email: e.target.value })} placeholder="Email khách hàng" /></F>
            <F label="Giới tính"><Select value={cust.gender ?? undefined} onChange={(v) => setC({ gender: v ?? null })} allowClear options={GENDERS} placeholder="Chọn giới tính" /></F>
            <F label="Ngày sinh">
              <DatePicker value={cust.dob ? dayjs(cust.dob) : null} onChange={(d) => setC({ dob: d ? d.format('YYYY-MM-DD') : null })} format="DD/MM/YYYY" placeholder="DD/MM/YYYY" className="w-full" />
            </F>
            <F label="Nguồn gốc khách" full>
              <Checkbox checked={cust.isForeign} onChange={(e) => setC({ isForeign: e.target.checked })}>Khách nước ngoài</Checkbox>
            </F>
            <F label="Tỉnh/Thành phố" required={!cust.isForeign}>
              <Select
                value={cust.province}
                loading={loadingProvinces}
                onChange={(v) => { setC({ province: v ?? null, ward: null }); if (v) loadWards(v); }}
                options={provinceOptions}
                placeholder="Vui lòng chọn"
                showSearch
                allowClear
                optionFilterProp="label"
              />
            </F>
            <F label="Phường/Xã" required={!cust.isForeign}>
              <Select value={cust.ward} loading={loadingWards} onChange={(v) => setC({ ward: v ?? null })} options={wardOptions} placeholder="Vui lòng chọn" showSearch allowClear optionFilterProp="label" disabled={!cust.province} />
            </F>
            <F label="Địa chỉ" required full><Input prefix={<MapPin size={13} className="text-gray-400" />} value={cust.address} onChange={(e) => setC({ address: e.target.value })} placeholder="Địa chỉ" /></F>
          </div>
        )}
      </Sec>

      {/* Lịch hẹn */}
      <Sec title="Lịch hẹn">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <F label="Chi nhánh" required>
            <Select value={branchId} onChange={setBranchId} options={refs?.branches} loading={!refs} placeholder="Vui lòng chọn" showSearch allowClear optionFilterProp="label" />
          </F>
          {!mang0 && (
            <F label="Mảng" required>
              <Select value={mang} onChange={setMang} placeholder="Chọn mảng" options={MANGS.map((m) => ({ value: m, label: m }))} />
            </F>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-[12px] font-medium text-gray-500 dark:text-gray-400"><span className="mr-0.5 text-red-500">*</span>Thời gian hẹn</span>
          <div className="flex flex-wrap items-center gap-2">
            <DatePicker
              value={day}
              open={pickerOpen}
              onOpenChange={(o) => {
                // Antd tự đóng khi bấm một ngày → giữ mở để chọn tiếp giờ
                if (!o && keepOpen.current) { keepOpen.current = false; return; }
                setPickerOpen(o);
              }}
              onChange={(d) => { if (d) { keepOpen.current = true; pickDay(d); setTimeout(() => { keepOpen.current = false; }, 0); } }}
              disabledDate={(d) => d.isBefore(today, 'day')}
              format={(d) => `${WD_FULL[d.day()]}, ${d.format('DD/MM/YYYY')}${slot !== undefined ? ` · ${slotLabel(slot)}` : ''}`}
              allowClear={false}
              inputReadOnly
              status={slot === undefined && err ? 'error' : undefined}
              className="w-[250px]"
              panelRender={(panel) => (
                <div className="flex">
                  {panel}
                  <div className="w-[236px] border-l border-gray-200 p-2.5 dark:border-gray-700">
                    <div className="mb-2 flex items-baseline justify-between gap-2">
                      <span className="text-[12.5px] font-semibold text-gray-700 dark:text-gray-200">Giờ hẹn · {WD_FULL[day.day()]} {day.format('DD/MM')}</span>
                      {branchName && <span className="text-[10.5px] text-gray-400">đã hẹn/{SLOT_CAP}</span>}
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                      {PERIODS.map(([label, from, to]) => (
                        <div key={label} className="flex flex-col gap-1">
                          <span className="text-center text-[11px] font-semibold uppercase text-gray-400">{label}</span>
                          {SLOTS.filter((m) => m >= from && m < to).map((m) => {
                            const past = day.add(m, 'minute').valueOf() <= now;
                            const n = load[m] ?? 0;
                            const full = n >= SLOT_CAP;
                            const on = slot === m;
                            return (
                              <Tooltip key={m} title={past ? 'Đã qua giờ' : full ? `Kín lịch · ${n}/${SLOT_CAP} khách` : n ? `${n}/${SLOT_CAP} khách đã hẹn khung này` : 'Còn trống'}>
                                <button
                                  type="button"
                                  aria-pressed={on}
                                  disabled={past || full}
                                  onClick={() => { setSlot(m); setErr(null); setPickerOpen(false); }}
                                  className={`h-6 rounded border text-[12px] font-semibold tabular-nums transition-colors disabled:cursor-not-allowed ${on
                                    ? 'border-[#3e91ab] bg-[#3e91ab] text-white'
                                    : full ? 'border-red-200 bg-red-50 text-red-300 line-through dark:border-red-500/30 dark:bg-red-500/10'
                                      : past ? 'border-transparent text-gray-300 dark:text-gray-600'
                                        : 'border-gray-200 text-gray-700 hover:border-[#3e91ab] hover:text-[#3e91ab] dark:border-gray-600 dark:text-gray-200'}`}
                                >
                                  {slotLabel(m)}
                                  {n > 0 && !full && !past && <span className={`ml-0.5 text-[10px] font-medium ${on ? 'text-white/80' : n >= SLOT_CAP - 1 ? 'text-amber-600' : 'text-gray-400'}`}>{n}</span>}
                                </button>
                              </Tooltip>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                    {!branchName && <div className="mt-2 text-[11px] text-gray-400">Chọn chi nhánh để xem số khách đã hẹn mỗi khung.</div>}
                  </div>
                </div>
              )}
            />
            {[['Hôm nay', today], ['Ngày mai', today.add(1, 'day')]].map(([label, d]) => {
              const on = (d as Dayjs).isSame(day, 'day');
              return (
                <button
                  key={label as string}
                  type="button"
                  aria-pressed={on}
                  onClick={() => { pickDay(d as Dayjs); setPickerOpen(true); }}
                  className={`h-8 rounded-md border px-3 text-[13px] font-medium transition-colors ${on
                    ? 'border-[#3e91ab] bg-[#3e91ab] text-white'
                    : 'border-gray-300 text-gray-700 hover:border-[#3e91ab] hover:text-[#3e91ab] dark:border-gray-600 dark:text-gray-200'}`}
                >
                  {label as string}
                </button>
              );
            })}
          </div>

          {slot === undefined && <span className="text-[11.5px] text-amber-600">Bấm vào ô ngày để chọn ngày và giờ hẹn.</span>}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <F label="Nhân viên trực page">{sel(refs?.page, staff, setStaff)}</F>
          <F label="Nhân viên telesale">{sel(refs?.tele, telesale, setTelesale)}</F>
          <F label="Nhân viên cơ sở">{sel(refs?.branchStaff, branchStaff, setBranchStaff)}</F>
        </div>
      </Sec>

      {/* Dịch vụ + chi phí */}
      <Sec title="Dịch vụ">
        {quotes.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-[#3e91ab]/30 bg-[#e8f4f7]/60 px-2.5 py-2 text-[12.5px] dark:bg-[#3e91ab]/10">
            <span className="text-gray-600 dark:text-gray-300">Theo báo giá</span>
            <Select
              size="small"
              value={quote?.id ?? ''}
              onChange={(v) => pickQuote(v || undefined)}
              options={[
                ...quotes.map((q) => ({ value: q.id, label: `${q.code} · ${q.items.length} dịch vụ · ${QUOTE_ST[q.status].label}` })),
                { value: '', label: 'Không theo báo giá' },
              ]}
              popupMatchSelectWidth={false}
              className="min-w-[220px]"
            />
            {quote && (
              <span className="text-gray-600 dark:text-gray-300">
                {quote.discount ? <>giảm {quote.discount}% → còn từ <b className="text-gray-900 dark:text-gray-50">{money(quoteAfter)}</b></> : <>từ <b className="text-gray-900 dark:text-gray-50">{money(quoteAfter)}</b></>}
                {quote.deposit && <> · cọc {money(quote.deposit.amount)}</>}
              </span>
            )}
          </div>
        )}
        {services.length > 0 && (
          <div className="overflow-hidden rounded-md border border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-[1fr_76px_100px_28px] gap-2 bg-gray-50 px-2.5 py-1.5 text-[11px] font-semibold uppercase text-gray-500 dark:bg-gray-900/40">
              <span>Sản phẩm</span><span>Số lượng</span><span className="text-right">Thành tiền</span><span />
            </div>
            {services.map((s) => (
              <div key={s.id} className="grid grid-cols-[1fr_76px_100px_28px] items-center gap-2 border-t border-gray-100 px-2.5 py-1.5 text-[12.5px] dark:border-gray-700">
                <span className="min-w-0 text-gray-800 dark:text-gray-100">
                  {s.name}
                  {s.id < 0 && <Tooltip title="Không tìm thấy trong danh mục dịch vụ — lịch trên hệ thống sẽ không có dịch vụ này"><Tag color="orange" className="!ml-1.5">Ngoài danh mục</Tag></Tooltip>}
                </span>
                <InputNumber size="small" min={1} value={s.qty} onChange={(v) => setServices((xs) => xs.map((x) => (x.id === s.id ? { ...x, qty: Number(v) || 1 } : x)))} className="w-full" />
                <span className="text-right tabular-nums">{vnd(s.price * s.qty)}</span>
                <button type="button" aria-label={`Bỏ ${s.name}`} onClick={() => setServices((xs) => xs.filter((x) => x.id !== s.id))} className="text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
        <Select
          value={null}
          showSearch
          filterOption={false}
          searchValue={svcQ}
          onSearch={setSvcQ}
          onChange={(v) => v != null && addSvc(v)}
          loading={svcLoading}
          placeholder="+ Thêm dịch vụ — gõ để tìm"
          notFoundContent={svcLoading ? <Spin size="small" /> : 'Không có dữ liệu'}
          options={svcOpts.map((s) => ({
            value: s.id,
            label: <div className="flex justify-between gap-2"><span className="truncate">{s.name}</span><span className="shrink-0 text-gray-500">{s.price.toLocaleString('vi-VN')}</span></div>,
          }))}
          className="w-full"
        />
        <div className="grid grid-cols-1 gap-3 rounded-md bg-gray-50 p-3 dark:bg-gray-900/40">
          <div className="flex items-center justify-between text-[13px]"><span className="text-gray-500">Tổng số tiền</span><b className="tabular-nums text-gray-900 dark:text-gray-50">{vnd(total)}</b></div>
          <F label="Ghi chú lịch hẹn">
            <Input.TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nhu cầu chính, rào cản, khách cần lưu ý gì khi đến" maxLength={500} />
          </F>
        </div>
      </Sec>

      {move && <Lbl label="Lý do dời"><Chips label="Lý do dời" list={CANCEL_REASONS.filter((x) => x !== 'Đặt nhầm')} value={reason} onChange={setReason} /></Lbl>}

      {clash && !move && (
        <Alert
          type="warning"
          showIcon
          message={`Khách đã có lịch ${dayjs(clash.at).format('HH:mm')} cùng ngày tại ${clash.branch}.`}
          description="Nếu khách đổi giờ, nên dời lịch đó để không bị trùng."
          action={<Button size="small" onClick={() => onSwitchMove(clash)}>Dời lịch đó</Button>}
        />
      )}

      <Checkbox checked={notify} onChange={(e) => setNotify(e.target.checked)}>
        <span className="text-[12.5px]">Gửi tin {move ? 'báo lịch mới' : 'xác nhận lịch'} qua Zalo</span>
      </Checkbox>

      {err && <Alert type="error" showIcon message={err} />}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3 dark:border-gray-700">
        <div className="min-w-0 text-[12.5px] text-gray-600 dark:text-gray-300">
          {at ? <><b className="text-gray-900 dark:text-gray-50">{at.format('HH:mm')} · {fullDay(at.valueOf())}</b>{branchName ? ` · ${branchName}` : ''}</> : <span className="text-gray-400">Chọn giờ hẹn</span>}
        </div>
        <div className="flex gap-2">
          <Button onClick={onClose} disabled={saving}>Huỷ</Button>
          <Button type="primary" onClick={save} loading={saving} className="!bg-[#3e91ab]">{move ? 'Lưu lịch mới' : sync ? 'Đưa lên hệ thống' : 'Tạo lịch hẹn'}</Button>
        </div>
      </div>
    </div>
  );
}

/* ---------- Tab ---------- */

type FormState = { move?: LeadAppt; base?: LeadAppt; sync?: boolean; quoteId?: string };

export default function ApptTab({ r, l, by, now, xung, mang, readOnly, openSignal, syncSignal, quoteId, onBooked, confirmTagId, onConfirmed }: {
  r: ChannelCustomerItem;
  l: LeadLocal;
  mang?: Mang;
  by: string;
  now: number;
  xung: string;
  readOnly?: boolean;
  /** Tăng lên mỗi lần bấm "Đặt lịch" ở đầu hồ sơ → mở form */
  openSignal: number;
  /** Lịch vừa đặt trong phiếu kết quả cuộc gọi (mới lưu trên máy) → mở form "Đưa lên hệ thống" cho lịch này */
  syncSignal?: { id: string; n: number };
  /** Mở form từ nút "Đặt lịch theo báo giá" → lấy dịch vụ của báo giá này */
  quoteId?: string;
  onBooked: (b: Booking, extra: Partial<LeadAppt>) => LeadAppt;
  /** Mã S6 - Xác nhận lịch hẹn: màn Lịch hẹn đọc trạng thái theo mã S của khách nên xác nhận phải đổi mã luôn */
  confirmTagId?: number;
  onConfirmed?: () => void;
}) {
  const { setApptStatus, moveAppt, patchAppt, addMsg, addTouch } = useLeadStore();
  const [form, setForm] = useState<FormState | null>(openSignal && !readOnly ? { quoteId } : null);
  const [cancel, setCancel] = useState<LeadAppt | null>(null);
  const [reason, setReason] = useState<string>();
  const [reasonNote, setReasonNote] = useState('');
  const [svTick, setSvTick] = useState(0);
  const sv = useServerAppts(r.id, svTick);
  const demo = isDemoId(r.id);
  const name = (r.display_name || r.fullname || '').trim();

  useEffect(() => { if (openSignal && !readOnly) setForm({ quoteId }); }, [openSignal, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const a = syncSignal && !readOnly && !isDemoId(r.id) ? l.appts.find((x) => x.id === syncSignal.id && !x.serverId) : undefined;
    if (a) setForm({ base: a, sync: true });
  }, [syncSignal?.n]); // eslint-disable-line react-hooks/exhaustive-deps

  const { up, past } = useMemo(() => splitAppts(l.appts, now), [l.appts, now]);
  const stats = apptStats(l.appts);
  // Lịch trên hệ thống đã có bản trên máy (đặt từ màn này) thì không hiện lặp
  const svOnly = useMemo(() => {
    const mine = new Set(l.appts.map((a) => a.serverId).filter(Boolean));
    return sv.items.filter((s) => !mine.has(s.id));
  }, [sv.items, l.appts]);
  const svUp = useMemo(() => svOnly.filter((s) => s.status !== 0 && dayjs(s.scheduled_at).valueOf() >= now - 2 * HOUR), [svOnly, now]);
  const history = useMemo(() => [
    ...past.map((a) => ({ at: a.at, a })),
    ...svOnly.filter((s) => !svUp.includes(s)).map((s) => ({ at: dayjs(s.scheduled_at).valueOf(), s })),
  ].sort((x, y) => y.at - x.at), [past, svOnly, svUp]);

  const sendConfirm = (a: LeadAppt) => {
    const X = xung.charAt(0).toUpperCase() + xung.slice(1);
    const text = (TPL.find((x) => x[0] === 'xl')?.[2] ?? '')
      .replace(/\{xung\}/g, xung).replace(/\{Xung\}/g, X).replace(/\{ten\}/g, name)
      .replace(/\{ngay\}/g, `${dayjs(a.at).format('HH:mm')} ${fullDay(a.at)}`)
      .replace(/Trần Hưng Đạo Q1/g, a.branch);
    addMsg(r.id, { from: 'n', text, by });
    addTouch(r.id, { kind: 'msg', text: `Zalo · gửi tin “Xác nhận lịch” ${dayjs(a.at).format('HH:mm DD/MM')}`, by });
  };

  /** Ghi lên hệ thống trước (khách mẫu thì bỏ qua) để lịch có ở danh sách Lịch hẹn; lỗi thì trả lỗi cho form */
  const save = async ({ booking: b, extra }: ApptSave, opt: { notify: boolean; reason?: string }): Promise<string | undefined> => {
    const mv = form?.move;
    const sy = form?.sync ? form.base : undefined;
    let server: Partial<LeadAppt> = {};
    if (!demo) {
      // Dời lịch đã có trên hệ thống thì sửa chính lịch đó, còn lại tạo lịch mới
      const res = mv?.serverId
        ? await putAppointment(mv.serverId, { ...extra.payload, status: 1 })
        : await postAppointment(extra.payload ?? {});
      if ('error' in res) return res.error;
      server = { serverId: res.id, serverCode: res.code ?? mv?.serverCode };
      setSvTick((t) => t + 1);
    }
    const code = server.serverCode ? ` · mã ${server.serverCode}` : '';
    if (sy) {
      patchAppt(r.id, sy.id, { ...extra, ...server, at: b.at!.valueOf(), branch: b.branch!, service: b.service || '', note: b.note });
      message.success(`Đã đưa lịch lên hệ thống${code}`);
      setForm(null);
      return undefined;
    }
    const a = mv
      ? moveAppt(r.id, mv.id, {
        at: b.at!.valueOf(), branch: b.branch!, mang: b.mang!, service: b.service || '', note: b.note, type: b.type, by,
        cust: { name, phone: r.phone, code: r.customer_code }, ...extra, ...server,
      }, opt.reason)
      : onBooked(b, { ...extra, ...server });
    if (!a) return undefined;
    message.success(mv ? `Đã dời lịch sang ${dayjs(a.at).format('HH:mm DD/MM')}${code}` : `Lịch đã có ở danh sách Lịch hẹn${code}`);
    if (opt.notify) sendConfirm(a);
    setForm(null);
    return undefined;
  };

  /** Đổi trạng thái lịch đã lên hệ thống (2 = đã xác nhận, 0 = huỷ); lỗi thì giữ nguyên trên máy */
  const pushStatus = async (a: LeadAppt, status: 0 | 2, extra: Record<string, unknown> = {}) => {
    if (!a.serverId || demo) return true;
    const res = await putAppointment(a.serverId, { customer_id: r.id, status, ...extra });
    if ('error' in res) { message.error(res.error); return false; }
    setSvTick((t) => t + 1);
    return true;
  };

  const confirmAppt = async (a: LeadAppt) => {
    if (!(await pushStatus(a, 2, confirmTagId ? { customer_tag_id: confirmTagId } : {}))) return;
    setApptStatus(r.id, a.id, 'confirmed', by);
    onConfirmed?.();
    message.success('Đã ghi khách xác nhận lịch');
  };

  const doCancel = async () => {
    if (!cancel || !reason) return;
    const why = reason === 'Khác' ? reasonNote.trim() || 'Khác' : reason;
    if (!(await pushStatus(cancel, 0))) return;
    setApptStatus(r.id, cancel.id, 'cancelled', by, { cancelReason: why });
    message.success('Đã huỷ lịch hẹn');
    setCancel(null);
  };

  const empty = !l.appts.length && !sv.items.length && !sv.loading;

  return (
    <div className="space-y-4">
      {!readOnly && (form ? (
        <ApptForm
          key={`${form.move?.id ?? ''}-${form.base?.id ?? ''}-${form.sync ? 's' : ''}-${form.quoteId ?? ''}`}
          r={r}
          l={l}
          now={now}
          mang={mang}
          move={form.move}
          base={form.base}
          sync={form.sync}
          quoteId={form.quoteId}
          onSwitchMove={(a) => setForm({ move: a })}
          onClose={() => setForm(null)}
          onSave={save}
        />
      ) : (
        <Button block type="dashed" onClick={() => setForm({})} icon={<CalendarPlus size={14} />} className="!h-10 font-semibold">
          {up.length ? 'Tạo thêm lịch hẹn' : 'Tạo lịch hẹn'}
        </Button>
      ))}

      {l.appts.length > 0 && (
        <div className="grid grid-cols-4 divide-x divide-gray-200 rounded-lg border border-gray-200 text-center dark:divide-gray-700 dark:border-gray-700">
          {([
            ['Tổng lịch', stats.total, ''],
            ['Đã đến', stats.came, 'text-emerald-600'],
            ['Không đến', stats.noshow, stats.noshow ? 'text-red-600' : ''],
            ['Huỷ / dời', stats.cancelled + stats.moved, ''],
          ] as const).map(([k, v, c]) => (
            <div key={k} className="px-1 py-2">
              <div className={`text-[16px] font-bold tabular-nums text-gray-800 dark:text-gray-100 ${c}`}>{v}</div>
              <div className="text-[11px] text-gray-500">{k}</div>
            </div>
          ))}
          {stats.rate !== null && (
            <div className="col-span-4 border-t border-gray-200 py-1.5 text-[12px] text-gray-500 dark:border-gray-700">
              Tỉ lệ đến hẹn <b className={stats.rate < 50 ? 'text-red-600' : 'text-emerald-600'}>{stats.rate}%</b>
              {stats.noshow >= 2 && <span className="text-red-600"> · khách đã lỡ hẹn {stats.noshow} lần, nên xác nhận kỹ</span>}
            </div>
          )}
        </div>
      )}

      {(up.length > 0 || svUp.length > 0) && (
        <section className="space-y-2">
          <h4 className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide text-gray-500"><CalendarClock size={13} />Sắp tới</h4>
          {up.map((a) => (
            <UpCard
              key={a.id}
              a={a}
              all={l.appts}
              now={now}
              readOnly={readOnly}
              onConfirm={() => confirmAppt(a)}
              onMove={() => setForm({ move: a })}
              onCancel={() => { setCancel(a); setReason(undefined); setReasonNote(''); }}
              onSync={demo ? undefined : () => setForm({ base: a, sync: true })}
            />
          ))}
          {svUp.length > 0 && <ul className="divide-y divide-gray-100 dark:divide-gray-700">{svUp.map((s) => <ServerRow key={s.id} s={s} now={now} />)}</ul>}
        </section>
      )}

      {(history.length > 0 || sv.loading || sv.failed) && (
        <section>
          <h4 className="mb-1 flex items-center justify-between text-[12px] font-semibold uppercase tracking-wide text-gray-500">
            <span>Lịch hẹn trước đó{history.length ? ` (${history.length})` : ''}</span>
            {sv.loading && <span className="flex items-center gap-1.5 font-normal normal-case tracking-normal"><Spin size="small" />Đang tải lịch trên hệ thống</span>}
            {sv.failed && <span className="font-normal normal-case tracking-normal text-amber-600">Chưa tải được lịch trên hệ thống</span>}
          </h4>
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {history.map((h) => ('a' in h && h.a ? (
              <PastRow
                key={h.a.id}
                a={h.a}
                all={l.appts}
                now={now}
                readOnly={readOnly}
                onNoShow={() => setApptStatus(r.id, h.a.id, 'noshow', by)}
                onRebook={() => setForm({ base: h.a })}
              />
            ) : 's' in h && h.s ? <ServerRow key={`sv-${h.s.id}`} s={h.s} now={now} /> : null))}
          </ul>
        </section>
      )}

      {empty && !form && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Khách chưa có lịch hẹn nào" />}

      <Modal
        open={!!cancel}
        title={cancel ? `Huỷ lịch ${dayjs(cancel.at).format('HH:mm DD/MM')}` : ''}
        okText="Huỷ lịch"
        cancelText="Không"
        okButtonProps={{ danger: true, disabled: !reason || (reason === 'Khác' && !reasonNote.trim()) }}
        onOk={doCancel}
        onCancel={() => setCancel(null)}
        destroyOnHidden
        width={440}
      >
        <div className="space-y-3">
          <Lbl label="Lý do huỷ"><Chips label="Lý do huỷ" list={CANCEL_REASONS} value={reason} onChange={setReason} /></Lbl>
          {reason === 'Khác' && <Input value={reasonNote} onChange={(e) => setReasonNote(e.target.value)} placeholder="Ghi rõ lý do" maxLength={120} autoFocus />}
          <div className="text-[12px] text-gray-500">Khách chỉ đổi giờ? Dùng <b>Dời lịch</b> để giữ liên kết với lịch cũ.</div>
        </div>
      </Modal>
    </div>
  );
}
